import React from 'react';

/**
 * A small circular initials chip used as a visual anchor in table rows. Derives
 * one or two initials from a name/email and tints deterministically so the same
 * entity keeps the same color. Keeps data tables from reading as plain text.
 */

// A few tint pairs [background, foreground]; chosen by a stable hash of the seed
// so a given supplier always renders the same color.
const PALETTE: [string, string][] = [
    ['#d1e8ff', '#033160'],
    ['#e9ebed', '#414d5c'],
];

function initialsOf(name: string): string {
    const cleaned = name.trim();
    if (!cleaned) return '?';
    const parts = cleaned.split(/[\s@._-]+/).filter(Boolean);
    if (parts.length === 0) return cleaned.slice(0, 1).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

function hash(seed: string): number {
    // Simple bounded string hash (no bitwise ops); kept small via modulo so it
    // stays within a safe integer range across long seeds.
    let h = 0;
    for (let i = 0; i < seed.length; i += 1) {
        h = (h * 31 + seed.charCodeAt(i)) % 1_000_000_007;
    }
    return h;
}

interface AvatarProps {
    name: string;
    /** Stable key for color selection; defaults to `name`. */
    seed?: string;
    size?: number;
}

export default function Avatar({ name, seed, size }: AvatarProps) {
    const [bg, fg] = PALETTE[hash(seed ?? name) % PALETTE.length];
    const dimension = size ?? 30;
    return (
        <span
            aria-hidden="true"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: dimension,
                height: dimension,
                borderRadius: '50%',
                background: bg,
                color: fg,
                fontSize: Math.round(dimension * 0.4),
                fontWeight: 700,
                lineHeight: 1,
            }}
        >
            {initialsOf(name)}
        </span>
    );
}

Avatar.defaultProps = {
    seed: undefined,
    size: 30,
};
